import { subscribeAppResume } from "./appResumeService";
import { apiRequest } from "./apiClient";
import { isDataSyncConnected } from "./dataSyncService";
import { localizeNotificationContent } from "../utils/notificationI18n";
import {
  getUserNotificationSettings,
} from "./notificationSettingsService";

// ─── localStorage dismissed — kısa kapanma/açmalarda silinen bildirimleri koru
const DISMISSED_KEY = "bp_dismissed_ids";
const DISMISSED_TTL = 180 * 24 * 60 * 60 * 1000; // 180 gün: silinenler build/restart sonrası geri düşmesin

function loadDismissedIds() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Map();
    const now = Date.now();
    const map = new Map();
    Object.entries(JSON.parse(raw)).forEach(([id, ts]) => {
      if (now - ts < DISMISSED_TTL) map.set(id, ts);
    });
    return map;
  } catch { return new Map(); }
}

function saveDismissedIds(map) {
  try {
    const obj = {};
    map.forEach((ts, id) => { obj[id] = ts; });
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(obj));
  } catch {}
}

const dismissedIds = loadDismissedIds();

export function dismissId(id) {
  if (!id) return;
  dismissedIds.set(String(id), Date.now());
  saveDismissedIds(dismissedIds);
}

export function dismissNotificationLocal(notification = {}) {
  const ids = [
    notification?.id,
    notification?._globalNotificationId,
    notification?.firebaseId,
    notification?.notificationId,
  ].filter(Boolean);
  ids.forEach(dismissId);
}

export function isDismissed(id) {
  const ts = dismissedIds.get(String(id));
  if (!ts) return false;
  if (Date.now() - ts > DISMISSED_TTL) { dismissedIds.delete(String(id)); return false; }
  return true;
}

// ─── Tip → ayar anahtarı ─────────────────────────────────────────────────────
export const typeToSettingKey = {
  chat: "chat",
  task: "tasks", taskDone: "tasks", taskReminder: "tasks",
  calendar: "calendar", calendarReminder: "calendar", birthday: "calendar",
  health: "health", medicine: "health", appointment: "health",
  shopping: "shopping",
  sharedExpense: "sharedExpenses",
  location: "location", yakinlik: "location", geofence: "location", bulusma: "location",
  finance: "finance",
  document: "documents",
  familyAnnouncement: "familyAnnouncements",
  familyStatusUpdated: "familyStatus", familyStatusUpdateRequest: "familyStatus",
  system: "familyAnnouncements", systemAnnouncement: "familyAnnouncements", scheduled: "familyAnnouncements",
};

const typeToPagePermission = {
  shopping: "shopping",
  task: "tasks", taskDone: "tasks", taskReminder: "tasks",
  calendar: "calendar", calendarReminder: "calendar", eventReminder: "calendar", birthday: "calendar",
  finance: "finance", sharedExpense: "sharedExpenses",
  health: "health", medicine: "health", appointment: "health", automationHealth: "health", cycle: "health",
  children: "children", chat: "chat", document: "documents",
  location: "harita", yakinlik: "harita", geofence: "harita", bulusma: "harita",
  assistant: "assistant", daily_summary: "assistant", urgent_alert: "assistant",
  weekly_digest: "assistant", smart_insight: "assistant",
};

const uyeSayfaBildirimiAlabilirMi = (uye, type) => {
  const pageKey = typeToPagePermission[type];
  if (!pageKey) return true;
  const role = String(uye?.aileYetkisi || uye?.role || "").trim().toLowerCase();
  if (["aile yöneticisi", "aile yoneticisi", "family admin", "admin", "owner", "yönetici", "yonetici"].includes(role)) return true;
  return uye?.permissions?.pages?.[pageKey] !== false;
};

// ─── Alıcı için bildirim gönderilebilir mi? ──────────────────────────────────
const bildirimGonderilebilirMi = async (familyId, aliciUid, type) => {
  if (!familyId || !aliciUid) return false;
  try {
    const settings = await getUserNotificationSettings(familyId, aliciUid);
    if (settings?.pushEnabled === false) return false;
    const settingKey = typeToSettingKey[type];
    // ÖNEMLİ: settings[settingKey] (örn. settings.tasks) aslında in_app_enabled
    // değerini taşır (bildirim merkezinde gösterilsin mi). PUSH kararı için doğru
    // alan settings.modulePush[settingKey] — modülün kendi push_enabled değeri.
    if (settingKey && settings?.modulePush?.[settingKey] === false) return false;
    // Anlık olay bildirimleri (mesaj, görev hareketi, aile içi işlem vb.) sessiz
    // saat kuyruğuna girmez. Sessiz saat yalnızca sunucudaki zamanlanmış/otomatik
    // bildirim motorlarında, alıcının yerel saatine göre uygulanır.
    return true;
  } catch {
    return true; // ayar okunamazsa yine de gönder
  }
};

// ─── Global forceRefresh ──────────────────────────────────────────────────────
const refreshListeners = new Set();
export const triggerNotificationRefresh = () => {
  refreshListeners.forEach((fn) => { try { fn(); } catch {} });
};

// ─── Push URL ─────────────────────────────────────────────────────────────────
const pushSayfaMap = {
  shopping: "alisveris", task: "gorev", calendar: "takvim", finance: "finans",
  sharedExpense: "harcama", document: "belgeler", familyAnnouncement: "ana",
  familyStatusUpdated: "ana", familyStatusUpdateRequest: "ana", familyStatus: "ana",
  system: "ana", systemAnnouncement: "ana", scheduled: "ana",
  health: "saglik", medicine: "saglik", birthday: "takvim",
  children: "cocuk", chat: "chat",
  assistant: "assistant", daily_summary: "assistant", urgent_alert: "assistant",
  weekly_digest: "assistant", smart_insight: "assistant",
  location: "harita", yakinlik: "harita", geofence: "harita", bulusma: "harita",
};

const pushUrlOlustur = ({ type, kaynak, kaynakId }) => {
  const hedefSayfa = pushSayfaMap[type] || pushSayfaMap[kaynak] || "ana";
  const params = new URLSearchParams();
  params.set("pushPage", hedefSayfa);
  if (type === "chat" && kaynakId) params.set("roomId", kaynakId);
  if (type === "familyStatusUpdated" || type === "familyStatusUpdateRequest") params.set("action", "familyStatus");
  if (kaynakId) params.set("sourceId", kaynakId);
  return `/?${params.toString()}`;
};

// ─── Dil yardımcısı ───────────────────────────────────────────────────────────
const normalizeDil = (l) => (l === "en" ? "en" : "tr");
const kullaniciDilCache = new Map();
const kullaniciDiliGetir = (uid) => kullaniciDilCache.get(uid) || "tr";

const bildirimMetniHazirla = ({ baslik, aciklama, baslikKey, baslikVars, aciklamaKey, aciklamaVars }, lang = "tr") =>
  localizeNotificationContent({ baslik, aciklama, baslikKey, baslikVars, aciklamaKey, aciklamaVars }, normalizeDil(lang));

// ─── Tekil bildirim ekle ──────────────────────────────────────────────────────
export const bildirimEkleService = async ({
  familyId, aliciUid,
  baslik, aciklama,
  baslikKey = null, baslikVars = null,
  aciklamaKey = null, aciklamaVars = null,
  type, kaynak = null, kaynakId = null,
  gonderenUid = null, idempotencyKey = null,
  familyName = null,
}) => {
  if (!familyId || !aliciUid) return;

  // Alıcının ayarlarını kontrol et — kapalıysa gönderme
  const gonderilebilir = await bildirimGonderilebilirMi(familyId, aliciUid, type);
  if (!gonderilebilir) return;

  const metin = bildirimMetniHazirla(
    { baslik, aciklama, baslikKey, baslikVars, aciklamaKey, aciklamaVars },
    kullaniciDiliGetir(aliciUid)
  );

  try {
    await apiRequest(`/notifications/${familyId}/user/${aliciUid}`, {
      method: "POST",
      body: JSON.stringify({
        baslik: metin.baslik, aciklama: metin.aciklama,
        baslikKey, baslikVars, aciklamaKey, aciklamaVars,
        type, kaynak, kaynakId, gonderenUid, idempotencyKey,
        url: pushUrlOlustur({ type, kaynak, kaynakId }),
        familyName: familyName || null,
        sendPush: true,
      }),
    });
    triggerNotificationRefresh();
  } catch (err) { console.warn("[bildirimEkleService]", err); }
};

// ─── Tüm aile üyelerine bildirim ─────────────────────────────────────────────
export const bildirimHerkeseYazService = async ({
  familyId, uyeler = [], ekleyenUid,
  baslik, aciklama,
  baslikKey = null, baslikVars = null,
  aciklamaKey = null, aciklamaVars = null,
  type, kaynak = null, kaynakId = null,
  gonderenUid = null, idempotencyKey = null,
  familyName = null,
}) => {
  if (!familyId) return;

  // Her alıcı için ayar kontrolü yap — kapalı olanları filtrele
  const hedefler = uyeler
    .map((u) => ({ ...u, uid: u.uid || u.id }))
    .filter((u) => u.uid && u.uid !== ekleyenUid && uyeSayfaBildirimiAlabilirMi(u, type));

  const gonderilebilecekler = [];
  await Promise.all(
    hedefler.map(async (u) => {
      const gonderilebilir = await bildirimGonderilebilirMi(familyId, u.uid, type);
     
      if (gonderilebilir) gonderilebilecekler.push(u.uid);
    })
  );


  // Hiç alıcı yoksa gönderme
  if (gonderilebilecekler.length === 0 && hedefler.length > 0) { return; }

  // Hedef listesi biliniyorsa (uyeler geçildiyse) ama kimse kalmadıysa gönderme
  // uyeler geçilmemişse (boş array) backend tüm üyelere gönderir — bu kasıtlı
  if (uyeler.length > 0 && gonderilebilecekler.length === 0) { return; }

  try {
   
    const result = await apiRequest(`/notifications/${familyId}/broadcast`, {
      method: "POST",
      body: JSON.stringify({
        baslik, aciklama, baslikKey, baslikVars, aciklamaKey, aciklamaVars,
        type, kaynak, kaynakId,
        gonderenUid: gonderenUid || ekleyenUid || null, ekleyenUid,
        targets: gonderilebilecekler.length > 0
          ? gonderilebilecekler
          : (uyeler.length > 0 ? [] : undefined),
        idempotencyKey, url: pushUrlOlustur({ type, kaynak, kaynakId }),
        familyName: familyName || null,
        sendPush: true,
      }),
    });
   
    triggerNotificationRefresh();
  } catch (err) { console.warn("[bildirimHerkeseYazService] HATA:", err?.message || err); }
};

export const bildirimTakvimTopluAktarimService = async ({ familyId, ekleyenUid, tamAd, count, idempotencyKey }) => {
  if (!familyId || !count) return;
  await apiRequest(`/notifications/${familyId}/broadcast`, {
    method: "POST",
    body: JSON.stringify({
      baslik: "📅 Aile takvimi güncellendi",
      aciklama: `${tamAd} seçili cihaz takvimlerinden ${count} etkinliği aile takvimine ekledi.`,
      baslikKey: "calendar.bulkImport.title",
      aciklamaKey: "calendar.bulkImport.body",
      aciklamaVars: { name: tamAd, count },
      type: "calendar", kaynak: "calendar", ekleyenUid, gonderenUid: ekleyenUid,
      idempotencyKey, url: "/?pushPage=takvim", sendPush: true,
    }),
  });
  triggerNotificationRefresh();
};

// ─── Bildirimleri getir ───────────────────────────────────────────────────────
export const bildirimleriGetirService = async (familyId, userUid, limit = 100) => {
  if (!familyId || !userUid) return [];
  try {
    const data = await apiRequest(
      `/notifications/${familyId}/list?userUid=${userUid}&limit=${limit}&includeGlobal=true`
    );
    return (data?.notifications || []).filter((n) => !isDismissed(n.id));
  } catch (err) { console.warn("[bildirimleriGetirService]", err); return []; }
};

// ─── Polling dinleyici ────────────────────────────────────────────────────────
export const bildirimleriDinleService = (familyId, userUid, callback) => {
  if (!familyId || !userUid) return () => {};
  let active = true;
  let timer = null;
  let requestInFlight = false;
  let sseConnected = isDataSyncConnected(familyId);
  let currentItems = [];

  const DISCONNECTED_POLL_MS = 30000;

  const scheduleNext = () => {
    clearTimeout(timer);
    if (!active) return;
    if (!sseConnected) timer = setTimeout(fetchAndNotify, DISCONNECTED_POLL_MS);
  };

  const fetchAndNotify = async () => {
    if (!active) return;
    if (typeof document !== "undefined" && document.hidden) {
      scheduleNext();
      return;
    }
    // focus + SSE durum değişimi aynı anda gelirse duplicate notification GET açma.
    if (requestInFlight) return;
    requestInFlight = true;
    try {
      const items = await bildirimleriGetirService(familyId, userUid);
      if (active) {
        currentItems = items;
        callback(currentItems);
      }
    } finally {
      requestInFlight = false;
      scheduleNext();
    }
  };

  const forceRefreshFn = () => {
    if (active) fetchAndNotify();
  };

  const onResumeBildirim = () => {
    if (active) fetchAndNotify();
  };

  const onSseStatus = (event) => {
    const detail = event?.detail || {};
    if (String(detail.familyId || "") !== String(familyId)) return;
    const nextConnected = detail.connected === true;
    if (sseConnected === nextConnected) return;
    sseConnected = nextConnected;
    if (sseConnected) {
      clearTimeout(timer);
    } else {
      scheduleNext();
    }
  };

  const onDataReconcile = (event) => {
    if (String(event?.detail?.familyId || "") === String(familyId)) fetchAndNotify();
  };

  const onDataChanged = (event) => {
    const detail = event?.detail || {};
    if (detail.resource !== "notifications") return;
    if (String(detail.familyId || "") !== String(familyId)) return;
    const item = detail.item;
    if (!item || isDismissed(item.id)) return;
    currentItems = [item, ...currentItems.filter((entry) => String(entry?.id) !== String(item.id))].slice(0, 100);
    callback(currentItems);
  };

  fetchAndNotify();
  refreshListeners.add(forceRefreshFn);
  const unsubscribeResume = subscribeAppResume(onResumeBildirim, { delayMs: 900 });
  window.addEventListener("fp:sseStatus", onSseStatus);
  window.addEventListener("fp:dataChanged", onDataChanged);
  window.addEventListener("fp:dataReconcile", onDataReconcile);

  return () => {
    active = false;
    clearTimeout(timer);
    unsubscribeResume();
    window.removeEventListener("fp:sseStatus", onSseStatus);
    window.removeEventListener("fp:dataChanged", onDataChanged);
    window.removeEventListener("fp:dataReconcile", onDataReconcile);
    refreshListeners.delete(forceRefreshFn);
  };
};

// ─── Okundu ──────────────────────────────────────────────────────────────────
export const bildirimOkunduIsaretle = async (familyId, userUid, bildirimId) => {
  if (!bildirimId) return;
  try { await apiRequest(`/notifications/item/${bildirimId}/read`, { method: "PATCH" }); }
  catch (err) { console.warn("[bildirimOkunduIsaretle]", err); }
};

export const bildirimKaynakOkunduIsaretle = async (familyId, userUid, kaynakId) => {
  if (!familyId || !kaynakId) return;
  try {
    await apiRequest(`/notifications/${familyId}/read-source`, {
      method: "POST", body: JSON.stringify({ userUid, kaynakId }),
    });
  } catch (err) { console.warn("[bildirimKaynakOkunduIsaretle]", err); }
};

export const tumunuOkunduYap = async (familyId, userUid) => {
  if (!familyId || !userUid) return;
  try {
    await apiRequest(`/notifications/${familyId}/all-read`, {
      method: "PATCH", body: JSON.stringify({ userUid }),
    });
  } catch (err) { console.warn("[tumunuOkunduYap]", err); }
};

// ─── Silme ───────────────────────────────────────────────────────────────────
export const bildirimSilService = async (familyId, userUid, bildirimId) => {
  if (!bildirimId) return;
  dismissId(bildirimId); // anında localStorage'a yaz
  try { await apiRequest(`/notifications/item/${bildirimId}`, { method: "DELETE" }); }
  catch (err) { console.warn("[bildirimSilService]", err); }
};

export const okunmuslariSilService = async (familyId, userUid) => {
  if (!familyId || !userUid) return;
  try {
    await apiRequest(
      `/notifications/${familyId}/bulk?userUid=${userUid}&onlyRead=true`,
      { method: "DELETE" }
    );
    // UI zaten optimistik temizlenir; polling dismissed filtresinden geçtiği için anlık geri düşmez.
  } catch (err) { console.warn("[okunmuslariSilService]", err); }
};

export const tumBildirimleriSilService = async (familyId, userUid, sadeceChat = false) => {
  if (!familyId || !userUid) return;
  const params = new URLSearchParams({ userUid });
  if (sadeceChat) params.set("sadeceChat", "true");
  try {
    await apiRequest(`/notifications/${familyId}/bulk?${params}`, { method: "DELETE" });
    // UI zaten optimistik temizlenir; polling dismissed filtresinden geçtiği için anlık geri düşmez.
  } catch (err) { console.warn("[tumBildirimleriSilService]", err); }
};

export const genelBildirimleriSilService = async (familyId, userUid) => {
  if (!familyId || !userUid) return;
  try {
    await apiRequest(
      `/notifications/${familyId}/bulk?userUid=${userUid}&excludeChat=true`,
      { method: "DELETE" }
    );
    // UI zaten optimistik temizlenir; polling dismissed filtresinden geçtiği için anlık geri düşmez.
  } catch (err) { console.warn("[genelBildirimleriSilService]", err); }
};

export const sohbetBildirimleriniSilService = async (familyId, userUid, roomId) => {
  if (!familyId || !userUid) return;
  const params = new URLSearchParams({ userUid, sadeceChat: "true" });
  if (roomId) params.set("roomId", roomId);
  try {
    await apiRequest(
      `/notifications/${familyId}/bulk?${params}`,
      { method: "DELETE" }
    );
    // UI zaten optimistik temizlenir; polling dismissed filtresinden geçtiği için anlık geri düşmez.
  } catch (err) { console.warn("[sohbetBildirimleriniSilService]", err); }
};
